import { useEffect, useState } from 'react';

import { AUDIO_LEVEL_ANALYSER_SETTINGS } from '@eluve/mic-utils';

import { MicrophoneLevelVisualization } from './MicrophoneLevelVisualization';

const UPDATE_INTERVAL_MS = 100;

interface MicrophoneLevelProps {
  stream?: MediaStream | null;
  variant?: 'default' | 'compact';
  className?: string;
}

/**
 * Container component that analyzes a MediaStream and displays audio level visualization.
 * Uses MicrophoneLevelVisualization for presentation.
 */
export const MicrophoneLevel: React.FC<MicrophoneLevelProps> = ({
  stream,
  variant = 'default',
  className,
}) => {
  const [waveformData, setWaveformData] = useState<number[]>([]);

  useEffect(() => {
    if (!stream) {
      setWaveformData([]);
      return;
    }

    const audioContext = new AudioContext();
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = AUDIO_LEVEL_ANALYSER_SETTINGS.fftSize;
    analyser.smoothingTimeConstant =
      AUDIO_LEVEL_ANALYSER_SETTINGS.smoothingTimeConstant;
    analyser.minDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.minDecibels;
    analyser.maxDecibels = AUDIO_LEVEL_ANALYSER_SETTINGS.maxDecibels;
    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const source = audioContext.createMediaStreamSource(stream);
    source.connect(analyser);

    const draw = () => {
      analyser.getByteFrequencyData(dataArray);
      setWaveformData(Array.from(dataArray));
    };

    draw();
    const intervalId = window.setInterval(draw, UPDATE_INTERVAL_MS);

    return () => {
      window.clearInterval(intervalId);
      audioContext.close();
      source.mediaStream.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, [stream]);

  return (
    <MicrophoneLevelVisualization
      waveformData={waveformData}
      variant={variant}
      className={className}
    />
  );
};
